import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { ROUTES, SCHUTZBEDARFSANALYSE_MODULES } from '../../../../config/constants';
import { getProzesse, getAnalysen, getRisiken, getAnwendungen, getMassnahmen } from '../../../../services/schutzbedarfsanalyseService';
import { getGapAnalysen } from '../../../../services/gapAnalyseService';
import { exportSchutzbedarfsanalyseToWord } from '../../../../services/schutzbedarfsanalyseWordExport';
import { exportGapAnalyseToWord } from '../../../../services/gapAnalyseWordExport';
import { exportRisikomanagementToWord } from '../../../../services/risikomanagementWordExport';
import logo from '../../../../assets/logo.png';
import './SchutzbedarfsanalyseHeader.css';

const SchutzbedarfsanalyseHeader = ({ currentModule, moduleLabel }) => {
    const navigate = useNavigate();
    const [exporting, setExporting] = useState(false);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const { user, selectedTenant } = useAuth();

    const getMandantName = () => {
        if (selectedTenant?.name) {
            return selectedTenant.name;
        }

        try {
            const tenantData = localStorage.getItem('selectedTenant');
            if (tenantData) {
                const tenant = JSON.parse(tenantData);
                return tenant.name || tenant.mandant_name || 'Unbekannter Mandant';
            }
        } catch (e) {
            console.error('Fehler beim Lesen des Mandanten:', e);
        }
        return 'Regentra Kunde';
    };

    const getUsername = () => {
        if (user) {
            const fullName = `${user.user_vorname || ''} ${user.user_name || ''}`.trim();
            if (fullName) return fullName;
            return user.username || user.email || 'Benutzer';
        }

        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const userObj = JSON.parse(userData);
                const fullName = `${userObj.user_vorname || ''} ${userObj.user_name || ''}`.trim();
                if (fullName) return fullName;
                return userObj.username || userObj.email || 'Benutzer';
            }
        } catch (e) {
            console.error('Fehler beim Lesen des Users:', e);
        }
        return 'Benutzer';
    };

    const handleExportSchutzbedarfsanalyse = async () => {
        setExporting(true);
        setShowExportDropdown(false);

        try {
            const [prozesseResult, analysenResult, anwendungenResult] = await Promise.all([
                getProzesse(),
                getAnalysen(),
                getAnwendungen()
            ]);

            if (!prozesseResult.success || !analysenResult.success || !anwendungenResult.success) {
                alert('Fehler beim Laden der Daten für den Export');
                setExporting(false);
                return;
            }

            await exportSchutzbedarfsanalyseToWord({
                prozesse: prozesseResult.data,
                analysen: analysenResult.data,
                anwendungen: anwendungenResult.data,
                mandantName: getMandantName(),
            });
        } catch (error) {
            console.error('Word-Export Fehler:', error);
            alert('Fehler beim Erstellen des Word-Dokuments: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    const handleExportGapAnalyse = async () => {
        setExporting(true);
        setShowExportDropdown(false);

        try {
            const [gapsResult, prozesseResult, anwendungenResult] = await Promise.all([
                getGapAnalysen(),
                getProzesse(),
                getAnwendungen()
            ]);

            if (!gapsResult.success || !prozesseResult.success || !anwendungenResult.success) {
                alert('Fehler beim Laden der Daten für den Export');
                setExporting(false);
                return;
            }

            await exportGapAnalyseToWord({
                gaps: gapsResult.data,
                prozesse: prozesseResult.data,
                anwendungen: anwendungenResult.data,
                mandantName: getMandantName(),
            });
        } catch (error) {
            console.error('GAP-Export Fehler:', error);
            alert('Fehler beim Erstellen des Word-Dokuments: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    const handleExportRisikomanagement = async () => {
        setExporting(true);

        try {
            const [risikenResult, massnahmenResult] = await Promise.all([
                getRisiken(),
                getMassnahmen()
            ]);

            if (!risikenResult.success || !massnahmenResult.success) {
                alert('Fehler beim Laden der Daten für den Export');
                setExporting(false);
                return;
            }

            await exportRisikomanagementToWord({
                risiken: risikenResult.data,
                massnahmen: massnahmenResult.data,
                mandantName: getMandantName(),
            });
        } catch (error) {
            console.error('Risiko-Export Fehler:', error);
            alert('Fehler beim Erstellen des Word-Dokuments: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    // Check if we're in Schutzbedarfsanalyse module (not Risikomanagement)
    const isSchutzbedarfsanalyseModule = currentModule !== SCHUTZBEDARFSANALYSE_MODULES.RISIKOMANAGEMENT;

    return (
        <div className="schutzbedarfsanalyse-header">
            <div className="header-left">
                <div
                    className="logo-container"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                >
                    <img src={logo} alt="Regentra Logo" className="header-logo" />
                </div>
                <div className="header-title">
                    <h1>{moduleLabel}</h1>
                    <p className="header-subtitle">BSI IT-Grundschutz konforme Analyse</p>
                </div>
            </div>

            <div className="header-right">
                {/* DROPDOWN für Schutzbedarfsanalyse, normaler Button für Risiko */}
                {isSchutzbedarfsanalyseModule ? (
                    <div className="export-dropdown-container">
                        <button
                            onClick={() => setShowExportDropdown(!showExportDropdown)}
                            disabled={exporting}
                            className="btn-export"
                        >
                            {exporting ? (
                                <>
                                    <span className="spinner"></span>
                                    Exportiere...
                                </>
                            ) : (
                                <>
                                    Word Export ▼
                                </>
                            )}
                        </button>

                        {showExportDropdown && !exporting && (
                            <div className="export-dropdown-menu">
                                <button
                                    className="export-dropdown-item"
                                    onClick={handleExportSchutzbedarfsanalyse}
                                >
                                    📄 Schutzbedarfsanalyse
                                </button>
                                <button
                                    className="export-dropdown-item"
                                    onClick={handleExportGapAnalyse}
                                >
                                    📊 GAP-Analyse
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        onClick={handleExportRisikomanagement}
                        disabled={exporting}
                        className="btn-export"
                    >
                        {exporting ? (
                            <>
                                <span className="spinner"></span>
                                Exportiere...
                            </>
                        ) : (
                            <>
                                Word Export
                            </>
                        )}
                    </button>
                )}

                <button
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                    className="back-to-dashboard-btn"
                >
                    ← Zurück zum Dashboard
                </button>

                <div className="header-info">
                    <div className="mandant-info">
                        <span className="info-label">Mandant:</span>
                        <span className="info-value">{getMandantName()}</span>
                    </div>
                    <div className="user-info">
                        <span className="info-label">Benutzer:</span>
                        <span className="info-value">{getUsername()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchutzbedarfsanalyseHeader;